import * as Sentry from "@sentry/nextjs"
import { useCallback } from "react"

import useToast from "@hooks/useToast"
import { ActivityDetailsFormAttributes } from "@modules/tenants/OrdersPage/ViewOrderPage/Components/ActivityRescheduleModal/ActivityDetailsForm"
import { getAddBookableProductToCartPayload } from "@mono/essentials/entities/cart/helpers"
import { useUpdateProductOfOrderItemMutation } from "@mono/essentials/entities/order/hooks"
import {
  GetOrderDetailsResponseType,
  ProductType,
  UpdateQuantityOfOrderItemRequestType,
} from "@mono/essentials/types/OrderTypes"

export type TDynamicRentalDetailsFormAttributes = {
  date?: string
  time?: string
  duration_type?: {
    id?: string
    type?: string
    min?: number
    max?: number
  }
  quantity?: number
  available_option?: string
  start_date?: string
  end_date?: string
  start_time?: string
  end_time?: string
  slot_id?: string
  units: {
    unitName?: string
    unitId?: string
    unitCount: number
    start_at: any
    end_at: any
    availability_id: string
  }[]
  product_availability_id?: string
}

export const useHandleRebookWithAnotherProduct = () => {
  const { showToast } = useToast()
  const { mutateAsync: updateProductOfOrderItemMutation } = useUpdateProductOfOrderItemMutation({})

  const handleRebookWithAnotherProduct = useCallback(
    async ({
      productInfo,
      selectedProduct,
      orderItemId,
      onSuccess,
      onError,
      orderData,
    }: {
      selectedProduct: ProductType
      productInfo: ActivityDetailsFormAttributes | TDynamicRentalDetailsFormAttributes
      orderItemId: string
      orderData: GetOrderDetailsResponseType
      onSuccess?: () => Promise<void>
      onError?: (error: unknown) => Promise<void>
    }) => {
      try {
        if (selectedProduct.type?.is_rental) {
          const unitsWithQuantity = productInfo?.units?.filter((unit) => unit.unitCount)
          if (unitsWithQuantity?.length) {
            const payload = getAddBookableProductToCartPayload({
              selectedProduct,
              productInfo,
            }) as UpdateQuantityOfOrderItemRequestType
            await updateProductOfOrderItemMutation({
              orderId: orderData.id,
              orderItemId,
              payload,
            })
            await onSuccess?.()
          } else {
            showToast({
              heading: "Unable to update the booking! please try again later.",
              type: "danger",
            })
          }
        } else {
          if (productInfo) {
            const payload = getAddBookableProductToCartPayload({
              selectedProduct,
              productInfo,
            }) as UpdateQuantityOfOrderItemRequestType
            await updateProductOfOrderItemMutation({
              orderId: orderData.id,
              orderItemId,
              payload,
            })
            await onSuccess?.()
          } else {
            showToast({
              heading: "Unable to update the booking! please try again later.",
              type: "danger",
            })
          }
        }
      } catch (error) {
        Sentry.captureException(error)
        await onError?.(error)
      }
    },
    [showToast, updateProductOfOrderItemMutation]
  )

  return { handleRebookWithAnotherProduct }
}
